import { FunctionComponent, useCallback, useEffect, useState, useRef, useMemo } from 'react'
import styled from 'styled-components'
import BoxContainer from '../../../fragments/BoxContainer'
import { useAuthStore } from '../../../stores/auth/useAuthStore'
import Text from '../../../components/Text'
import Button from '../../../components/Button'
import SvgFragment from '../../../fragments/SvgFragment'
import ListItem, { IListItemProps } from '../../../components/list/ListItem'
import Column from '../../../components/Column'
import Row from '../../../components/Row'
import { styles } from "../../../styles/themes/style";
import { formatNumber, isChromeBasedBrowser, isMobileDevice, isValidEmail } from '../../../utils'
import { getSyncContactsTooltip } from '../../../utils/syncContactsTooltips'
import { useTasksHandlers } from '../../../services/actions/tasks/useTasksHandlers'
import { ITaskResponse } from '../../../interfaces/response/task'
import { useTasksStore } from '../../../stores/tasks/useTasksStore'
import { useTasksActions } from '../../../stores/tasks/useTasksActions'
import { useNavigate } from 'react-router-dom'
import { AppRoutes, SidebarTypes } from '../../../interfaces/store/appStore'
import { useAuthHandlers } from '../../../services/actions/auth/useAuthHandlers'
import { useAppHandlers } from '../../../services/actions/app/useAppHandlers'
import { useAppStore } from '../../../stores/application/useAppStore'
import { useExternalHandlers } from '../../../services/actions/navigation/useExternalHandlers'
import { useAnalyticsHandlers } from '../../../services/actions/analytics/useAnalyticsHandlers'
import { ReactComponent as OnboardingSvg } from '../../../assets/img/Onboarding.svg'
import { ReactComponent as RoadmapSvg } from '../../../assets/img/Roadmap.svg'
import { ReactComponent as SupportSvg } from '../../../assets/img/Support.svg'
import TasksTable from '../components/home/HomeTasksTable'
import moment from 'moment'
import { useInterval } from '../../../wrappers/IntervalContext'
import ActionText from '../../../components/ActionText'
import LoadingSpinner from '../../../components/LoadingSpinner'
import HomeBirthdateTable from '../components/home/HomeBirthdateTable'
import HomeJobChangesTable from '../components/home/HomeJobChangesTable'
import { useConnectionsHandlers } from '../../../services/actions/connections/useConnectionsHandlers'
import { useConnectionsRequest } from '../../../services/apis/connections/useConnectionsRequest'
import { useNotificationsRequest } from '../../../services/apis/notifications/useNotificationsRequest'
import { useConnectionsStore } from '../../../stores/connections/useConnectionsStore'
import { shouldSuppressLegacySyncConnectionsModal } from '../../onboarding/onboardingSurveyUtils'
import { triggerManualLinkedInSync } from '../../onboarding/linkedinConnectFlowUtils'
import { getLastSyncAtValue, isHomeSyncInProgress } from '../components/home/homeSyncStateUtils'
import TooltipWrapper from '../../../components/TooltipWrapper'

interface IHomeSectionProps { }

interface IAppTaskProps extends IListItemProps {
    $svgWhite?: boolean
    $grey?: boolean
    $green?: boolean
    $blue?: boolean
    $primary?: boolean
    $surface?: boolean
    disabled?: boolean
    spaceBetween?: boolean
    $stacked?: boolean
}

const ConnectionsCounter = styled.button`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 2px;
    padding: 8px 16px;
    min-width: 140px;
    border: none;
    border-radius: 8px;
    background: ${styles.colors.primary100};
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
        background: ${styles.colors.primary200};
    }

    &:focus {
        outline: none;
    }
`

const ConnectionsCount = styled.span`
    font-family: NunitoSans;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    color: ${styles.colors.primary600};
`

const ConnectionsCountLabel = styled.span`
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: ${styles.colors.black500};
    white-space: nowrap;
`

const HomeSection: FunctionComponent<IHomeSectionProps> = () => {
    useEffect(() => {
        document.title = "LeadDelta Home";
      }, []);
    const { store: { user, workspace, authData } } = useAuthStore()
    const { getHomePageTasks, getAndResolveTasksHandler } = useTasksHandlers()
    const { syncConnectionsHandler, getUserHandler, syncConversationsHandler, sendDesktopLinkHandler } = useAuthHandlers()
    const { setSyncConnectionsModalHandler, setConnectionsOverviewModal, setDestructiveModal, hideModal, setRemovalOverviewModal, setAddMembersModal, openSidebar } = useAppHandlers()
    const { store: { extensionInstalled, requestLoading, onboardingStep } } = useAppStore()
    const { visitChromeStore, visitChromeBrowserDownload, openOnboarding, openRoadmap, chatWithUs } = useExternalHandlers()
    const { store: { tasks } } = useTasksStore()
    const { setTasks } = useTasksActions()
    const [tasksLoading, setTasksLoading] = useState(true);
    const navigate = useNavigate()
    const { getTotalConnectionsOverviewAnalytics } = useAnalyticsHandlers()
    const { store: { scheduledRemovalParameters }} = useConnectionsStore()
    const { startInterval } = useInterval();
    const { cancelConnectionsRemovalHandler, getConnectionByPublicIdentifierHandler, messageClickHandler, getMultipleConditionsFiltersHandler } = useConnectionsHandlers()
    const { getConnectionsHighlights } = useConnectionsRequest()
    const { getNotifications } = useNotificationsRequest()

    const showChromeExtensionHomeCards = isChromeBasedBrowser() && !isMobileDevice()
    const isMobile = isMobileDevice()

    const sendDesktopLinkClick = useCallback(() => {
        void sendDesktopLinkHandler('home')
    }, [sendDesktopLinkHandler])

    const externalVideosSection = [
        {
            heading: 'Welcome to LeadDelta',
            label: 'How to organize your network, and build stronger professional relationships.',
            onClick: () => window.open('https://www.loom.com/embed/e8fdd0aef606407a984379515b119789?sid=44f3f1e6-2217-42a2-9948-a94cee931e1f', '_blank')
        },
        {
            heading: 'LeadDelta brief demo video',
            label: 'Effortlessly organize your network, and unlock meaningful relationships solo or as a team..',
            onClick: () => window.open('https://www.loom.com/embed/a0d1b3e8230c4882b91bc3e203c86d09', '_blank')
        },
        {
            eyebrow: 'BOOK A DEMO',
            heading: 'How to use LeadDelta as a team?',
            label: 'How teams of 5 or 555 use LeadDelta to activate full potential of their organization. Book a demo.',
            onClick: () => window.open('https://meetings.hubspot.com/ved/enterprise', '_blank')
        }
    ]
    const birthDateFilterSearch = {
        birthDateFrom: moment().utc().startOf('day').toDate(),
        birthDateTo: moment().utc().endOf('day').toDate(),
        isAutotagsFilterOr: 'yes',
        selectedUsersMutual: [
            {
                _id: user._id,
                firstName: user?.firstName,
                lastName: user?.lastName
            }
        ],
        autotagsFilter: [
            {
                "_id": "New Connection",
                "title": "New Connection",
                "color": "#5964ff",
                "isForbidden": true
            },
            {
                "_id": "LinkedIn 1st",
                "title": "LinkedIn 1st",
                "color": "#5964ff",
                "isForbidden": true
            }
        ]
    }

    const jobChangesFilterSearch = {
        page: 1,
        pageSize: 4,
        hasJobChanges: 'yes',
        hasCompanyChanges: 'yes'
    }

    // const [connectionsOverviewModal, setConnectionsOverviewModal] = useState<boolean>(false)
    const [ analytics, setAnalytics ] = useState<any>({
        'connections-total': 0,
        "connections-imported-csv-total": 0,
        'connections-updated': 0,
        'connections-updated-connected-to-linkedin': 0,
        "connections-disconnected-total": 0
    })
    const [birthdateConnections, setBirthdateConnections] = useState<any>([])
    const [jobChangeConnections, setJobChangeConnections] = useState<any>([])
    const [connectionsHighlightsLoading, setConnectionsHighlightsLoading] = useState<boolean>(true)
    const [syncRequested, setSyncRequested] = useState(false)
    const tasksLoadingRef = useRef(false);
    const hasCalledForMultipleConditions = useRef(false);
    const lastSyncAt = getLastSyncAtValue(user?.lastSyncAt)
    const syncInProgress = useMemo(() => (
        !user?.isSynced && (syncRequested || isHomeSyncInProgress(workspace?.initialSync, lastSyncAt))
    ), [user?.isSynced, syncRequested, workspace?.initialSync, lastSyncAt])
    
    useEffect(() => {
        if(workspace) {
            reloadTasksHandler();
        }
        getTotalConnectionsOverviewAnalyticsHandler()
    }, [workspace])

    // Load connection filter options (tags, languages, etc.) once on mount
    useEffect(() => {
        if (!hasCalledForMultipleConditions.current) {
            getMultipleConditionsFiltersHandler();
            hasCalledForMultipleConditions.current = true;
        }
    }, [getMultipleConditionsFiltersHandler])

    useEffect(() => {
        if (user?.isSynced || workspace?.initialSync) {
            setSyncRequested(false)
        }
    }, [user?.isSynced, workspace?.initialSync])

    useEffect(() => {
        if (shouldSuppressLegacySyncConnectionsModal(user, onboardingStep)) return
        if (isMobile) return
        if(extensionInstalled && user?.linkedinData?.publicIdentifier && !user?.isSynced && user?.preferences?.hideDemoVideo
            && !moment().isBefore(moment(lastSyncAt).add(20, 'minutes'))
        ){
            setLastSyncAt()
        }
    }, [extensionInstalled, user, onboardingStep])

    const loadTasksStateHandler = useCallback(async (tasks: ITaskResponse[]) => {
        await getAndResolveTasksHandler(tasks)
        setTasks(tasks);
        setTasksLoading(false);
        tasksLoadingRef.current = false;
    }, [getAndResolveTasksHandler, setTasks])

    const reloadTasksHandler = useCallback(async (resolved?: boolean) => {
        // Prevent duplicate calls only if not resolved (i.e., not from loadTasksStateHandler)
        if (tasksLoadingRef.current && !resolved) {
            return;
        }
        
        // Set loading flag only for initial calls (not resolved)
        if (!resolved) {
            tasksLoadingRef.current = true;
        }
        
        setTasksLoading(true);

        const forbiddenTasks = await getHomePageTasks(true);
        let tasks = forbiddenTasks;

        if (forbiddenTasks.length < 5) {
            const otherTasks = await getHomePageTasks();
            const filteredOtherTasks = otherTasks.filter(
                otherTask => !forbiddenTasks.some(forbiddenTask => forbiddenTask._id === otherTask._id)
            );
            tasks = [...forbiddenTasks, ...filteredOtherTasks.slice(0, 5 - forbiddenTasks.length)];
        }

        if (tasks.length && !resolved) {
            loadTasksStateHandler(tasks);
        } else {
            setTasks(tasks);
            setTasksLoading(false);
            tasksLoadingRef.current = false;
        }
    }, [getHomePageTasks, loadTasksStateHandler, setTasks]);

    const setLastSyncAt = useCallback(async() => {
        if (!extensionInstalled || !user || isMobile) return

        const runSync = () => {
            setSyncRequested(true)
            syncConnectionsHandler(user, false, authData)
            if (!user?.isSynced) {
                startInterval(async () => getUserHandler(), 300000)
            }
            syncConversationsHandler()
        }

        triggerManualLinkedInSync({
            user,
            extensionInstalled,
            onboardingStep,
            onSync: runSync,
            onLegacySyncModal: () => setSyncConnectionsModalHandler(() => runSync()),
        })
    }, [user, extensionInstalled, authData, onboardingStep, isMobile, getUserHandler, setSyncConnectionsModalHandler, startInterval, syncConnectionsHandler, syncConversationsHandler])

    const getTotalConnectionsOverviewAnalyticsHandler = useCallback( async () => {
        const statistics = await getTotalConnectionsOverviewAnalytics();
        let statisticsObject = statistics.reduce((accumulator, value) => ({...accumulator, ...{[value.version]: value.value}}), {});
        setAnalytics({...analytics, ...statisticsObject})
    }, [])

    const setConnectionsOverviewModalHandler = async () => {
        await getTotalConnectionsOverviewAnalyticsHandler()
        // setConnectionsOverviewModal(true)
    }
    const onCancelConnectionsRemovalHandler = async () => {
        setDestructiveModal({
            headingText: "Are you sure you want to cancel contacts removal?",
            descriptionText: `${scheduledRemovalParameters?.count} out of ${scheduledRemovalParameters?.total} contacts removed.`,
            confirmBtnTitle: 'Yes, Cancel Removal',
            onCancelHandler: () => hideModal(),
            onConfirmHandler: async () => {
                hideModal()
                // TODO - stop executing disconnect
                cancelConnectionsRemovalHandler();
            }
        })
    }

    const getConnectionsHighlightsHandler = useCallback(async () => {
        const result = await getConnectionsHighlights({
            birthdays: {
                page: 1,
                pageSize: 4,
                birthDateFrom: birthDateFilterSearch.birthDateFrom,
                birthDateTo: birthDateFilterSearch.birthDateTo,
                isAutotagsFilterOr: birthDateFilterSearch.isAutotagsFilterOr,
                autotagsFilter: birthDateFilterSearch.autotagsFilter.map(tag => tag._id).join(','),
                usersMutual: birthDateFilterSearch.selectedUsersMutual.map(user => user._id).join(',')
                
            },
            jobChanges: {
                page: 1,
                pageSize: 4,
                hasJobChanges: jobChangesFilterSearch.hasJobChanges,
                hasCompanyChanges: jobChangesFilterSearch.hasCompanyChanges
            }
        })
        if(result) {
            setBirthdateConnections(result.birthdays.connections)
            setJobChangeConnections(result.jobChanges.connections)
        }
    }, [])

    useEffect(() => {
        if('getConnectionsHighlights' in requestLoading) setConnectionsHighlightsLoading(requestLoading?.['getConnectionsHighlights'])
    }, [requestLoading]);

    useEffect(() => {
        if (user?.isSynced) {
            getConnectionsHighlightsHandler()
        }
    }, [user?.isSynced, getConnectionsHighlightsHandler])

    useEffect(() => {
        getConnectionsHighlightsHandler();
    }, [])

    return <Container>
        <HomeMainSection>
            <HomeTopBlock>
                <HeaderRow>
                    <Column gap="4px">
                        <Text $black $heading2 $bold $capitalize={!isValidEmail(user?.firstName)}>
                            {isValidEmail(user?.firstName) ? 'Hey there 👋' : `Hi, ${user?.firstName}!`}
                        </Text>
                        <Text $lighter $paragraph2>{`Let's work your network.`}</Text>
                    </Column>
                    <ConnectionsCounter
                        type="button"
                        onClick={() => setConnectionsOverviewModal(analytics)}
                    >
                        <ConnectionsCount>{formatNumber(analytics['connections-total'])}</ConnectionsCount>
                        <ConnectionsCountLabel>{'Number of connections'}</ConnectionsCountLabel>
                    </ConnectionsCounter>
                </HeaderRow>
                <BannerStack>
                {isMobile && (
                    <AppTask $primary $stacked spaceBetween>
                        <Column gap="4px">
                            <Text $white $bold $paragraph1>{'Finish on your computer'}</Text>
                            <BannerSubtitle>
                                Your LeadDelta workspace runs through the Chrome extension, which needs a laptop or desktop. Mobile support is coming — for now, hop on your computer for the full experience.
                            </BannerSubtitle>
                        </Column>
                        <BannerCta type="button" onClick={sendDesktopLinkClick}>
                            {'Send me the link'}
                            <SvgFragment type="arrowRight" />
                        </BannerCta>
                    </AppTask>
                )}
                {!isMobile && !showChromeExtensionHomeCards && (
                    <AppTask $primary spaceBetween>
                        <Column gap="4px">
                            <Text $white $bold $paragraph1>{'Install LeadDelta Chrome App'}</Text>
                            <BannerSubtitle>
                                Get Google Chrome to sync your LinkedIn contacts and experience full productivity boost.
                            </BannerSubtitle>
                        </Column>
                        <BannerCta type="button" onClick={() => visitChromeBrowserDownload()}>
                            {'Get Google Chrome'}
                            <SvgFragment type="arrowRight" />
                        </BannerCta>
                    </AppTask>
                )}
           {showChromeExtensionHomeCards && !extensionInstalled && <AppTask $primary spaceBetween>
                    <Column gap="4px">
                        <Text $white $bold $paragraph1>{'Install LeadDelta Chrome App'}</Text>
                        <BannerSubtitle>{'Experience full productivity boost and data enrichment.'}</BannerSubtitle>
                    </Column>
                    <TooltipWrapper tooltipText={getSyncContactsTooltip(false)}>
                        <BannerCta type="button" onClick={() => {
                            try { sessionStorage.setItem('ld_extension_install_source', 'home_banner') } catch {}
                            visitChromeStore()
                        }}>
                            {'Install'}
                            <SvgFragment type="arrowRight" />
                        </BannerCta>
                    </TooltipWrapper>
                </AppTask>
            }
            {showChromeExtensionHomeCards && !(workspace?.initialSync || syncInProgress) && 
            <AppTask $primary disabled={!extensionInstalled} spaceBetween>
                    <Column gap="4px">
                        <Text $white $bold $paragraph1>{'Sync your LinkedIn contacts'}</Text>
                        <BannerSubtitle>
                            {extensionInstalled
                                ? 'Auto-sync your LinkedIn contacts daily.'
                                : getSyncContactsTooltip(false)}
                        </BannerSubtitle>
                    </Column>
                    <TooltipWrapper tooltipText={getSyncContactsTooltip(extensionInstalled)}>
                        <BannerCta type="button" disabled={!extensionInstalled} onClick={() => extensionInstalled && setLastSyncAt()}>
                            {'Sync Contacts'}
                            <SvgFragment type="arrowRight" />
                        </BannerCta>
                    </TooltipWrapper>
                </AppTask>}
                <AppTask disabled={workspace?.isStarter} spaceBetween $surface $stacked={isMobile}>
                    <Column gap="4px">
                        <Text $black $bold $paragraph1>{'Friends can search each other’s contacts.'}</Text>
                        <Text $lighter>{'You can invite your coworkers, investors, friends… peers or your community members.'}</Text>
                    </Column>
                    <TooltipWrapper tooltipText={workspace?.isStarter ? 'Upgrade to Pro or Business to unlock this feature' : ''}>
                        <BannerCta $outline type="button" disabled={workspace?.isStarter} onClick={() => !workspace?.isStarter && setAddMembersModal()}>
                            {'Add members'}
                            <SvgFragment type="arrowRight" />
                        </BannerCta>
                    </TooltipWrapper>
                </AppTask>
                {scheduledRemovalParameters?.inProgress
                    && (scheduledRemovalParameters?.total > scheduledRemovalParameters?.count)
                    && (scheduledRemovalParameters?.dailyLimit > scheduledRemovalParameters?.dailyCount) &&
                    <AppTask $grey>
                        <LoadingSpinner />
                        <Column>
                            <StyledText>
                                <Text $bold>{`${scheduledRemovalParameters?.total} LinkedIn contacts are scheduled to be removed.`}</Text>
                            </StyledText>
                            <StyledText>
                                <Text $lighter>{`Successfully removed ${scheduledRemovalParameters?.count} LinkedIn contacts.`}
                                    <ActionText onClickHandler={() => setRemovalOverviewModal()}>Removal overview</ActionText>
                                    <ActionText danger onClickHandler={() => onCancelConnectionsRemovalHandler()}>Cancel Removal</ActionText>
                                </Text>
                            </StyledText>
                        </Column>
                    </AppTask>
                }
                </BannerStack>
            </HomeTopBlock>
        </HomeMainSection>

        <HomePanelsRow>
            <HomePanel gap="16px">
                <PanelHeaderRow>
                    <PanelTitle>{'Your tasks'}</PanelTitle>
                    <ActionText onClickHandler={() => navigate(AppRoutes.TASKS)}>{'Manage tasks'}</ActionText>
                </PanelHeaderRow>
                <TasksTable tasks={tasks} tasksLoading={tasksLoading} />
            </HomePanel>
            <HomePanel gap="16px">
                <PanelHeaderRow>
                    <PanelTitle>{'Today’s birthdays'}</PanelTitle>
                    <ActionText
                        $disabled={connectionsHighlightsLoading || !birthdateConnections.length || !user?.isSynced}
                        onClickHandler={() => navigate(AppRoutes.CONNECTIONS, { state: birthDateFilterSearch })}
                    >
                        {'See all'}
                    </ActionText>
                </PanelHeaderRow>
                <HomeBirthdateTable
                    birthdateConnections={birthdateConnections}
                    birthdateConnectionsLoading={connectionsHighlightsLoading}
                    isSynced={!!user?.isSynced}
                    syncInProgress={syncInProgress}
                    extensionInstalled={extensionInstalled}
                    onSyncContacts={setLastSyncAt}
                />
            </HomePanel>
        </HomePanelsRow>
        <StyledBoxedContainer $height='340px'>
            <PanelHeaderRow>
                <PanelTitle>{'Recent job changes'}</PanelTitle>
                {jobChangeConnections.length > 0 && user?.isSynced && (
                    <ActionText
                        onClickHandler={() => navigate(AppRoutes.CONNECTIONS, {
                            state: {
                                ...jobChangesFilterSearch,
                                hasJobChanges: jobChangesFilterSearch.hasJobChanges,
                                hasCompanyChanges: jobChangesFilterSearch.hasCompanyChanges,
                            },
                        })}
                    >
                        {'See all'}
                    </ActionText>
                )}
            </PanelHeaderRow>
            <HomeJobChangesTable
                jobChangeConnections={jobChangeConnections}
                jobChangeConnectionsLoading={connectionsHighlightsLoading}
                isSynced={!!user?.isSynced}
                syncInProgress={syncInProgress}
                extensionInstalled={extensionInstalled}
                onSyncContacts={setLastSyncAt}
            />
        </StyledBoxedContainer>
        <StyledBoxedContainer>
            <Row>
                {externalVideosSection.map((section: any) => (
                    <ExternalVideosSection onClick={section.onClick} >
                        <Text $heading6>{section.eyebrow ?? 'Watch a video'}</Text>
                        <Row spaceBetween>
                            <Text className='external-videos-section-title' $heading5 $bold>{section.heading}</Text>
                            <SvgFragment type='openExternal' />
                        </Row>
                        <Text $paragraph3>{section.label}</Text>
                    </ExternalVideosSection>
                ))}
            </Row>
        </StyledBoxedContainer>
        <StyledBoxedContainer>
            <HeadingRow><Text $heading4>{'Help center'}</Text></HeadingRow>
            <HelpCenterRow gap="16px" $isMobile={isMobile}>
                <StyledSupport gap="12px" $isMobile={isMobile} onClick={()=>openOnboarding()}>
                    <OnboardingSvg />
                    <Column gap="8px">
                        <Text $heading4>{'Onboarding'}</Text>
                        <Text $lighter>{'Just joined LeadDelta? Discover how to maximize your experience and unlock its full potential.'}</Text>
                    </Column>
                </StyledSupport>
                <StyledSupport gap="12px" $isMobile={isMobile} onClick={()=>openRoadmap()}>
                    <RoadmapSvg />
                    <Column gap="8px">
                        <Text $heading4>{'Roadmap'}</Text>
                        <Text $lighter>{'We build for you! Share your thoughts and contribute your ideas to shape the future of LeadDelta.'}</Text>
                    </Column>
                </StyledSupport>
                <StyledSupport gap="12px" $isMobile={isMobile} onClick={()=>chatWithUs()}>
                    <SupportSvg />
                    <Column gap="8px">
                        <Text $heading4>{'Support'}</Text>
                        <Text $lighter>{'Need help? Explore our support hub and helpful articles or connect with our LeadDelta support team.'}</Text>
                    </Column>
                </StyledSupport>
            </HelpCenterRow>
        </StyledBoxedContainer>
    </Container>
}

const Container = styled.div`
    width: 100%;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    height: 100vh;
    overflow-y: auto;
    align-items: center;
`
const StyledBoxedContainer = styled(BoxContainer)<{ $height?: string }>`
    max-width: 1024px;
    border-radius: 12px;
    ${({$height}) => $height && `min-height: ${$height}`}
`

const HomeMainSection = styled(StyledBoxedContainer)`
    gap: 8px;
`

const HomeTopBlock = styled(Column)`
    width: 100%;
    gap: 16px;
`

const BannerStack = styled(Column)`
    width: 100%;
    gap: 8px;
`

const HeaderRow = styled(Row)`
    justify-content: space-between;
    align-items: center;
    width: 100%;
`

const WrapRow = styled(Row)`
    width: 100%;
    flex-wrap: wrap;
`

const HelpCenterRow = styled(Row)<{ $isMobile?: boolean }>`
    width: 100%;
    flex-wrap: wrap;

    ${({ $isMobile }) =>
        $isMobile &&
        `
        flex-direction: column;
        align-items: center;
        justify-content: center;
    `}
`

const HomePanelsRow = styled(Row)`
    width: 100%;
    max-width: 1024px;
    align-items: stretch;
    gap: 16px;
    flex-wrap: wrap;
`

const HomePanel = styled(Column)`
    flex: 1 1 calc(50% - 8px);
    min-width: 400px;
    height: 330px;
    padding: 16px;
    border-radius: 12px;
    background: ${styles.colors.white};
    overflow: hidden;
`

const PanelHeaderRow = styled(HeaderRow)`
    gap: 12px;
    flex-shrink: 0;
`

const PanelTitle = styled.h2`
    margin: 0;
    flex: 1;
    min-width: 0;
    font-family: NunitoSans, 'Nunito Sans', sans-serif;
    font-size: 18px;
    line-height: 24px;
    font-weight: 700;
    color: ${styles.colors.black600};
`

const BannerSubtitle = styled.span`
    font-family: NunitoSans;
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    color: #F6F5F7;
`

const BannerCta = styled.button<{ $outline?: boolean; disabled?: boolean }>`
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 6px 12px;
    height: 32px;
    width: auto;
    flex-shrink: 0;
    border-radius: 8px;
    border: ${({ $outline }) => ($outline ? `1px solid ${styles.colors.black200}` : 'none')};
    background: ${styles.colors.white};
    color: ${styles.colors.black500};
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    & .svg {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
    }
    & .path {
        fill: ${styles.colors.black500};
    }

    &:hover:not(:disabled) {
        background: ${styles.colors.black100};
    }

    &:disabled {
        opacity: 0.55;
        cursor: default;
    }
`

const AppTask = styled(ListItem)<IAppTaskProps>`
    padding: 10px 16px;
    border-radius: 4px;
    gap: 10px;
    align-items: center;
    justify-content: ${({ spaceBetween}) => (spaceBetween ? 'space-between' : 'flex-start')};
    ${({ $stacked }) => $stacked && `
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    `}
    ${({ $primary }) => $primary && `
        padding: 16px;
        border-radius: 12px;
        gap: 24px;
        background: ${styles.colors.primary600};
        border: none;
    `}
    ${({ $surface }) => $surface && `
        padding: 16px;
        border-radius: 12px;
        gap: 24px;
        background: ${styles.colors.white};
        border: 1px solid ${styles.colors.black200};
    `}
    ${({ $grey, theme: { quinaryColor, senaryColor } }) => $grey && `
        background: ${senaryColor};
        border: 1px solid ${quinaryColor};
    `}
    ${({ $svgWhite, theme: { quinaryColor, senaryColor } }) => $svgWhite && `
        & .svg .path {
            fill: white;
        }
    `}
    ${({ $blue }) => $blue && `
        background: ${styles.colors.blue200};
        border: 1px solid ${styles.colors.primary600};
        & .svg .path {
            fill: ${styles.colors.primary600}
        }
    `}
    ${({ $green }) => $green && `
        background: ${styles.colors.green200};
        border: 1px solid ${styles.colors.green600};
        & .svg .path {
            fill: ${styles.colors.green600}
        }
    `}
    ${({ disabled }) => disabled && `
        & .svg .path {
            fill: ${styles.colors.black200}
        }
    `}
`
const StyledSupport = styled(Column)<{ $isMobile?: boolean }>`
    width: 320px;
    ${({ $isMobile }) =>
        $isMobile &&
        `
        width: 100%;
        max-width: 320px;
    `}
    &:hover {
        opacity: 0.75;
        cursor: pointer;
    }
`
const HeadingRow = styled(Row)`
    width: 100%;
`
const StyledText = styled(Text)`
  display: inline-block;
  text-align: left;
`;
const StyledActionText = styled(ActionText)`
    display: flex;
    align-items: baseline;
    span {    
        color: ${styles.colors.black300};
        font-size: 12px;
    }

    &:hover{
        path { fill: ${styles.colors.primary400} }
    }

    div {
        margin-right: 4px;
        svg {
            margin-bottom: 4px;
        }
    }
`

const ExternalVideosSection = styled.div`
    width: calc(100% / 3);
    padding: 0 15px 0 15px;
    cursor: pointer;
    &:hover {
        svg path { fill: ${styles.colors.primary600}};
        .external-videos-section-title { color: ${styles.colors.primary600}};
    } 
`
export default HomeSection

